import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { getEfetiveEmpresaId } from './authCache'

export interface Empresa {
  id: string
  // Dados básicos
  razao_social: string
  nome_fantasia?: string
  cnpj: string
  inscricao_estadual?: string
  inscricao_municipal?: string
  codigo_atividade?: string
  natureza_juridica?: string
  data_fundacao?: string

  // Endereço
  cep?: string
  endereco?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  pais?: string

  // Contato
  telefone_principal?: string
  telefone_secundario?: string
  email_principal?: string
  email_financeiro?: string
  website?: string

  // Fiscal
  regime_tributario?: string
  crt?: string
  validade_certificado?: string
  codigo_municipio_ibge?: string
  optante_simples_nacional?: boolean
  regime_especial_tributacao?: string
  rbt12?: number
  natureza_operacao?: string
  incentivador_cultural?: boolean
  nfse_emitir_ibs_cbs?: boolean
  cnae_principal?: string
  cnaes_secundarios?: string[]

  // NFS-e
  nfse_padrao?: 'nacional' | 'elotech'
  nfse_ambiente?: 'producao' | 'homologacao'
  senha_ws?: string

  // NF-e
  nfe_ambiente?: 'producao' | 'homologacao'
  nfe_contingencia?: 'SVC-AN' | 'SVC-RS' | 'EPEC' | null
  id_csrt?: number | null
  csrt?: string | null
  ultimo_nsu?: string | null
  ultima_consulta_nfe?: string | null
  consulta_dfe_automatica?: boolean

  // NFC-e
  nfce_ambiente?: 'producao' | 'homologacao'
  nfce_id_token?: string
  nfce_csc?: string

  // Certificado Digital
  logo_url?: string
  certificado_digital_url?: string
  certificado_senha?: string

  // Séries Fiscais
  serie_nfe_id?: string | null
  serie_nfce_id?: string | null
  serie_nfse_id?: string | null

  // Ordem de Servico
  tagline?: string | null
  os_termos_impressao?: string | null

  // Controle
  ativo: boolean
  matriz: boolean

  // Auditoria
  criado_em: string
  atualizado_em: string
}

export interface CreateEmpresaData {
  razao_social: string
  nome_fantasia?: string
  cnpj: string
  inscricao_estadual?: string
  inscricao_municipal?: string
  codigo_atividade?: string
  natureza_juridica?: string
  data_fundacao?: string
  cep?: string
  endereco?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  estado?: string
  pais?: string
  telefone_principal?: string
  telefone_secundario?: string
  email_principal?: string
  email_financeiro?: string
  website?: string
  regime_tributario?: string
  crt?: string
  validade_certificado?: string
  codigo_municipio_ibge?: string
  optante_simples_nacional?: boolean
  regime_especial_tributacao?: string
  rbt12?: number
  natureza_operacao?: string
  incentivador_cultural?: boolean
  nfse_emitir_ibs_cbs?: boolean
  cnae_principal?: string
  cnaes_secundarios?: string[]
  nfse_padrao?: 'nacional' | 'elotech'
  nfse_ambiente?: 'producao' | 'homologacao'
  senha_ws?: string
  nfe_ambiente?: 'producao' | 'homologacao'
  nfe_contingencia?: 'SVC-AN' | 'SVC-RS' | 'EPEC' | null
  id_csrt?: number | null
  csrt?: string | null
  ultimo_nsu?: string | null
  ultima_consulta_nfe?: string | null
  consulta_dfe_automatica?: boolean
  nfce_ambiente?: 'producao' | 'homologacao'
  nfce_id_token?: string
  nfce_csc?: string
  logo_url?: string
  certificado_digital_url?: string
  certificado_senha?: string
  ativo?: boolean
  matriz?: boolean
  serie_nfe_id?: string | null
  serie_nfce_id?: string | null
  serie_nfse_id?: string | null
  tagline?: string | null
  os_termos_impressao?: string | null
}

export interface UpdateEmpresaData extends Partial<CreateEmpresaData> {}

export function useEmpresa() {
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Buscar todas as empresas
  const fetchEmpresas = async () => {
    try {
      setLoading(true)
      setError(null)

      const { data, error } = await supabase
        .from('empresas')
        .select('*')
        .order('criado_em', { ascending: false })

      if (error) {
        console.error('❌ Erro Supabase:', error)
        throw error
      }

      setEmpresas(data || [])
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido'
      console.error('❌ Erro ao buscar empresas:', err)
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Buscar empresa por ID
  const fetchEmpresaById = async (id: string): Promise<Empresa | null> => {
    try {
      const { data, error } = await supabase
        .from('empresas')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error

      return data
    } catch (err: unknown) {
      console.error('Erro ao buscar empresa por ID:', err)
      return null
    }
  }

  // Buscar empresa do usuário logado (prioridade) ou empresa matriz (fallback)
  const fetchEmpresaMatriz = useCallback(async (): Promise<Empresa | null> => {
    try {
      // Primeiro, verificar se temos empresa em cache (considera software_house)
      const usuarioEmpresaId = getEfetiveEmpresaId()

      if (usuarioEmpresaId) {
        const { data: empresaUsuario, error: errorUsuario } = await supabase
          .from('empresas')
          .select('*')
          .eq('id', usuarioEmpresaId)
          .eq('ativo', true)
          .single()

        if (!errorUsuario && empresaUsuario) {
          return empresaUsuario
        }
      }

      // Se não tem empresa do usuário, tentar buscar via auth
      const { data: { user } } = await supabase.auth.getUser()

      if (user) {
        const { data: usuario, error: usuarioError } = await supabase
          .from('usuarios')
          .select('empresa_id')
          .eq('auth_user_id', user.id)
          .eq('ativo', true)
          .single()

        if (!usuarioError && usuario?.empresa_id) {
          const { data: empresaUsuario, error: errorEmpresa } = await supabase
            .from('empresas')
            .select('*')
            .eq('id', usuario.empresa_id)
            .eq('ativo', true)
            .single()

          if (!errorEmpresa && empresaUsuario) {
            return empresaUsuario
          }
        }
      }

      // Fallback: buscar empresa matriz

      const { data, error } = await supabase
        .from('empresas')
        .select('*')
        .eq('matriz', true)
        .eq('ativo', true)
        .single()

      if (error) {
        // Se não encontrou matriz, buscar qualquer empresa ativa
        const { data: empresaAtiva, error: errorAtiva } = await supabase
          .from('empresas')
          .select('*')
          .eq('ativo', true)
          .limit(1)
          .single()

        if (errorAtiva) {
          console.error('❌ Nenhuma empresa ativa encontrada:', errorAtiva)
          return null
        }

        return empresaAtiva
      }

      return data
    } catch (err: unknown) {
      console.error('❌ Erro ao buscar empresa:', err)
      return null
    }
  }, [])

  // Criar nova empresa
  const createEmpresa = async (empresaData: CreateEmpresaData): Promise<Empresa | null> => {
    try {
      setError(null)

      // Se está marcando como matriz, desmarcar outras
      if (empresaData.matriz) {
        await supabase
          .from('empresas')
          .update({ matriz: false })
          .eq('matriz', true)
      }

      const { data, error } = await supabase
        .from('empresas')
        .insert([empresaData])
        .select()
        .single()

      if (error) {
        console.error('❌ Erro ao inserir:', error)
        throw error
      }

      // Atualizar lista local
      setEmpresas(prev => [data, ...prev])

      return data
    } catch (err: any) {
      console.error('❌ Erro ao criar empresa:', err)
      setError(err.message)
      return null
    }
  }

  // Atualizar empresa
  const updateEmpresa = async (id: string, empresaData: UpdateEmpresaData): Promise<Empresa | null> => {
    try {
      setError(null)

      // Se está marcando como matriz, desmarcar outras
      if (empresaData.matriz) {
        await supabase
          .from('empresas')
          .update({ matriz: false })
          .eq('matriz', true)
          .neq('id', id)
      }

      const { data, error } = await supabase
        .from('empresas')
        .update({
          ...empresaData,
          atualizado_em: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error

      // Atualizar lista local
      setEmpresas(prev => prev.map(empresa =>
        empresa.id === id ? data : empresa
      ))

      return data
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao atualizar empresa:', err)
      return null
    }
  }

  // Deletar empresa (soft delete)
  const deleteEmpresa = async (id: string): Promise<boolean> => {
    try {
      setError(null)

      const { error } = await supabase
        .from('empresas')
        .update({
          ativo: false,
          atualizado_em: new Date().toISOString()
        })
        .eq('id', id)

      if (error) throw error

      // Atualizar lista local
      setEmpresas(prev => prev.map(empresa =>
        empresa.id === id ? { ...empresa, ativo: false } : empresa
      ))

      return true
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao deletar empresa:', err)
      return false
    }
  }

  // Reativar empresa
  const reactivateEmpresa = async (id: string): Promise<boolean> => {
    try {
      setError(null)

      const { error } = await supabase
        .from('empresas')
        .update({
          ativo: true,
          atualizado_em: new Date().toISOString()
        })
        .eq('id', id)

      if (error) throw error

      // Atualizar lista local
      setEmpresas(prev => prev.map(empresa =>
        empresa.id === id ? { ...empresa, ativo: true } : empresa
      ))

      return true
    } catch (err: any) {
      setError(err.message)
      console.error('Erro ao reativar empresa:', err)
      return false
    }
  }

  // Verificar se CNPJ já existe
  const checkCNPJExists = async (cnpj: string, excludeId?: string): Promise<boolean> => {
    try {
      let query = supabase
        .from('empresas')
        .select('id')
        .eq('cnpj', cnpj)

      if (excludeId) {
        query = query.neq('id', excludeId)
      }

      const { data, error } = await query

      if (error) throw error

      return (data?.length || 0) > 0
    } catch (err: any) {
      console.error('Erro ao verificar CNPJ:', err)
      return false
    }
  }

  useEffect(() => {
    fetchEmpresas()
  }, [])

  return {
    empresas,
    loading,
    error,
    fetchEmpresas,
    fetchEmpresaById,
    fetchEmpresaMatriz,
    createEmpresa,
    updateEmpresa,
    deleteEmpresa,
    reactivateEmpresa,
    checkCNPJExists,
    setError
  }
}